import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Card, CardContent, Avatar, Button,
    Chip, CircularProgress, Stack, IconButton, Tooltip, Switch,
    Dialog, DialogTitle, DialogContent, TextField, DialogActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LayersIcon from '@mui/icons-material/Layers';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import EditIcon from "@mui/icons-material/Edit";
import CreateConnectorDialog from "./CreateConnectorDialog";
import axios from 'axios';
import OrderWsStatusChip from "./OrderWsStatusChip";

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const BrokerApiTiles = ({ connectors, onRefresh }) => {
    const [brokers, setBrokers] = useState({});
    const [sessionMap, setSessionMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState('');
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingConnector, setEditingConnector] = useState(null);

    const [openMultiplierDialog, setOpenMultiplierDialog] = useState(false);
    const [selectedConnector, setSelectedConnector] = useState(null);
    const [multiplierInput, setMultiplierInput] = useState('');

    const token = localStorage.getItem('token');
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const fetchSessions = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/user-connector-session/my/list`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const map = {};
            res.data.forEach(session => {
                map[session.connector_id] = session;
            });
            setSessionMap(map);
        } catch (err) {
            console.error("Failed to fetch connector sessions", err);
        }
    };

    const brokerLogin = async (connectorId) => {
        try {
            const res = await axios.get(`${API_BASE_URL}/user-connector/my/${connectorId}/connect`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const { redirect_url, tracking_id} = res.data;
            sessionStorage.setItem("broker_tracking_id", tracking_id);
            await fetchSessions();
            onRefresh();
            window.location.assign(redirect_url);
        } catch (err) {
            console.error("Failed to initiate broker connect", err);
            setSnackbarMsg("Connection failed");
            setSnackbarOpen(true);
        }
    };

    const brokerLogout = async (connectorId) => {
        try {
            await axios.delete(`${API_BASE_URL}/user-connector-session/by-connector/${connectorId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSnackbarMsg("Disconnected session");
            setSnackbarOpen(true);
            await fetchSessions();
            onRefresh();
        } catch (err) {
            console.error("Failed to disconnect session", err);
            setSnackbarMsg("Failed to disconnect");
            setSnackbarOpen(true);
        }
    };

    const deleteConnector = async (id) => {
        const confirmed = window.confirm("Are you sure you want to delete this connector?");
        if (!confirmed) return;

        try {
            await axios.delete(`${API_BASE_URL}/user-connector/my/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSnackbarMsg("Connector deleted");
            setSnackbarOpen(true);
            onRefresh();
        } catch (err) {
            console.error("Delete failed", err);
            setSnackbarMsg("Failed to delete connector");
            setSnackbarOpen(true);
        }
    };

    const handleToggleActive = async (connectorId, newState) => {
        try {
            await axios.patch(
                `${API_BASE_URL}/user-connector/set-active/${connectorId}`, {}, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { is_active: newState },
                });
            await fetchSessions();
            setSnackbarMsg(`Connector ${newState ? "activated" : "deactivated"}`);
            setSnackbarOpen(true);
            onRefresh();
        } catch (err) {
            console.error("Failed to update is_active", err);
            setSnackbarMsg("Failed to update connector state");
            setSnackbarOpen(true);
        }
    };

    const updateMultiplier = async () => {
        if (!selectedConnector || !multiplierInput || parseInt(multiplierInput) < 1) return;

        try {
            await axios.patch(`${API_BASE_URL}/user-connector/${selectedConnector._id}/multiplier`,{},
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { multiplier: parseInt(multiplierInput) }
                });
            setSnackbarMsg("Multiplier updated");
            setSnackbarOpen(true);
            setOpenMultiplierDialog(false);
            onRefresh();
        } catch (err) {
            console.error("Failed to update multiplier", err);
            setSnackbarMsg("Failed to update multiplier");
            setSnackbarOpen(true);
        }
    };

    const fetchBrokers = async () => {
        try {
            const brokerRes = await axios.get(`${API_BASE_URL}/brokers/list/active`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const brokerMap = {};
            brokerRes.data.forEach(b => {
                brokerMap[b._id] = {
                    brokerName: b.brokerName,
                    iconUrl: b.iconUrl
                };
            });
            setBrokers(brokerMap);
        } catch (err) {
            console.error("Failed to load brokers", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBrokers().then(() => fetchSessions());
    }, []);

    if (loading) return <CircularProgress />;

    return (
        <Box>
            <Typography variant="h6" gutterBottom>API Connectors</Typography>

            <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="info" onClose={() => setSnackbarOpen(false)}>{snackbarMsg}</Alert>
            </Snackbar>

            <Stack spacing={2}>
                {connectors.map((conn) => {
                    const broker = brokers[conn.broker_id] || {};
                    return (
                        <Card key={conn._id} variant="outlined">
                            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar src={broker.iconUrl} alt={broker.brokerName} sx={{ width: 48, height: 48 }} />
                                    <Box>
                                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                            <Chip label={conn.connector_name.toUpperCase()} size="small" sx={{
                                                bgcolor: '#e3f2fd',
                                                color: '#1565c0',
                                                fontWeight: 'bold',
                                                border: '1px solid #90caf9',
                                                    textTransform: 'uppercase'
                                            }} />
                                            {conn.is_active && (
                                                <Button
                                                    variant="contained"
                                                    color={
                                                        sessionMap[conn._id]
                                                            ? sessionMap[conn._id].status === "connected" ? "info"
                                                            : sessionMap[conn._id].status === "pending" || sessionMap[conn._id].status === "init" ? "warning"
                                                            : sessionMap[conn._id].status === "failed" ? "error" : "error"
                                                            : "success"
                                                    }
                                                    size="small"
                                                    onClick={() => {
                                                        sessionMap[conn._id]
                                                            ? brokerLogout(conn._id)
                                                            : brokerLogin(conn._id);
                                                    }}
                                                >
                                                    {sessionMap[conn._id]
                                                        ? sessionMap[conn._id].status.toUpperCase()
                                                        : "CONNECT ->"}
                                                </Button>
                                            )}
                                            <Box mt={1} display="flex" alignItems="center" justifyContent="space-between">
                                                <Typography variant="caption" color="text.secondary">
                                                    Order Tracker
                                                </Typography>
                                                <OrderWsStatusChip connectorId={conn._id} />
                                            </Box>
                                            <Chip label={broker.brokerName || "Unknown"} size="small" variant="outlined" />
                                            {sessionMap[conn._id]?.profile && (
                                                <Box mt={1} ml={9}>
                                                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                                        {Object.entries(sessionMap[conn._id].profile).map(([key, value]) => (
                                                            <li key={key}><Typography variant="body2"><strong>{key}</strong>: {String(value)}</Typography></li>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Stack>
                                    </Box>
                                </Stack>

                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Tooltip title="Time">
                                        <Chip icon={<AccessTimeIcon />} label="Time" color="warning" variant="outlined" />
                                    </Tooltip>
                                    <Tooltip title="Set Multiplier">
                                        <Chip
                                            icon={<LayersIcon />}
                                            label="Multiplier"
                                            color="success"
                                            variant="outlined"
                                            onClick={() => {
                                                setSelectedConnector(conn);
                                                setMultiplierInput(conn.multiplier || '');
                                                setOpenMultiplierDialog(true);
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </Tooltip>
                                    <Tooltip title="Edit">
                                        <IconButton color="primary" onClick={() => {
                                                setEditingConnector(conn);
                                                setEditDialogOpen(true);
                                        }}>
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton color="error" onClick={() => deleteConnector(conn._id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Switch
                                        checked={conn.is_active}
                                        onChange={(e) => handleToggleActive(conn._id, e.target.checked)}
                                        size="small"
                                    />
                                </Stack>
                            </CardContent>
                        </Card>
                    );
                })}
            </Stack>

            {/* Multiplier Input Dialog */}
            <Dialog open={openMultiplierDialog} onClose={() => setOpenMultiplierDialog(false)}>
                <DialogTitle>Set Multiplier</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Multiplier"
                        type="number"
                        fullWidth
                        value={multiplierInput}
                        inputProps={{ min: 1 }}
                        onChange={(e) => setMultiplierInput(e.target.value)}
                        margin="dense"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenMultiplierDialog(false)}>Cancel</Button>
                    <Button onClick={updateMultiplier} disabled={!multiplierInput || parseInt(multiplierInput) < 1}>Save</Button>
                </DialogActions>
            </Dialog>

            {editDialogOpen && editingConnector && (
                <CreateConnectorDialog
                    brokerId={editingConnector.broker_id}
                    mode="edit"
                    initialData={editingConnector}
                    open={editDialogOpen}
                    onClose={(success) => {
                        setEditDialogOpen(false);
                        setEditingConnector(null);
                        if (success) onRefresh();
                    }}
                />
            )}
        </Box>
    );
};

export default BrokerApiTiles;
